// === src/pages/BusinessModelBuilder.jsx (Option C — NO AUTOSAVE) ===

import React, { useEffect, useState, useRef } from "react";
import axios from "../utils/axiosInstance";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useNavigate, useLocation } from "react-router-dom";
import { triggerAIPopup } from "../utils/triggerAIPopup";
import AIPopupModal from "../components/AIPopupModal";
import learningLinks from "../data/learningLinks";
import { useIdea } from "../context/IdeaContext";
import { useAuth } from "../context/AuthContext";
import { PlayCircle,CheckCircle,Download,ArrowRight,Sparkles } from "lucide-react";
import ModuleHeader from "../components/ModuleHeader";
import { buildMDIHPdfName } from "../utils/pdfNaming";

/* ----------------------------------------------------------
    Utility → Convert logo to Base64 for PDF header
---------------------------------------------------------- */
const loadImageAsBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = function () {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = url;
  });
};

const BusinessModelBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const modelRef = useRef(null);

  // Context
  const {
    selectedIdea,
    businessModel,
    setBusinessModel,
    setUserId,
    userId,
    markModuleComplete,
  } = useIdea();

  /* RESOLVE STARTUP NAME (CONSISTENT WITH PLATFORM) */

    const startupName =

    selectedIdea?.title ||
    selectedIdea?.ideaTitle ||

    businessModel?.startupName ||

    "Startup";

  const { user } = useAuth();

  const [formData, setFormData] = useState({
    startupName: "",
    description: "",
    industry: "Technology",
  });

  const [localModel, setLocalModel] = useState(null);
  const [mvpPlan, setMvpPlan] = useState([]);
  const [mvpAgent, setMvpAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showProceedBtn, setShowProceedBtn] = useState(false);
  const [recordId, setRecordId] = useState(null);   // must exist
  const [loadedFromDB,setLoadedFromDB] = useState(false);

  /* ----------------------------------------------------------
     Sync userId
  ---------------------------------------------------------- */
  useEffect(() => {
    if (!userId && user?._id) {
      setUserId(user._id);
    } else if (!user && !userId) {
      navigate("/login");
    }
  }, [user, userId, setUserId, navigate]);

  const activeUserId = userId || user?._id;

  /* ----------------------------------------------------------
     Prefill Data
  ---------------------------------------------------------- */
  useEffect(() => {
    const prefill = async () => {
      try {
        if (location.state?.aiRefinedModel) {

            setLocalModel(location.state.aiRefinedModel);

            setBusinessModel(location.state.aiRefinedModel);

            setShowProceedBtn(true);

            setLoadedFromDB(false);   // ⭐ ADD

            }

        if (location.state?.mvpPlan) setMvpPlan(location.state.mvpPlan);
        if (location.state?.mvpAgent) setMvpAgent(location.state.mvpAgent);
          else if(businessModel?.mvpAgent){

          setMvpAgent(businessModel.mvpAgent);

          }


        const navIdea = location.state?.idea;
        const ideaId = selectedIdea?.ideaId || location.state?.ideaId;

        const descriptionValue =
          navIdea?.description ||
          selectedIdea?.solution || selectedIdea?.description ||
          navIdea ||
          selectedIdea?.ideaTitle ||
          "";

        setFormData((prev) => ({
          ...prev,
          startupName:
          selectedIdea?.ideaTitle ||
          selectedIdea?.title ||
          localStorage.getItem("activeIdeaTitle") ||
          prev.startupName ||
          "",
          description: descriptionValue,
        }));

        if (ideaId) {
          try {
            const r = await axios.get("/api/business-model/last", {
              params: { ideaId },
            });

            if (r.data?.success && r.data.businessModel && !localModel) {

              setLocalModel(r.data.businessModel);

              setBusinessModel(r.data.businessModel);

              setShowProceedBtn(true);

              setLoadedFromDB(true);

              if (r.data.recordId) setRecordId(r.data.recordId);

              }
          } catch {}
        }

        if (!localModel) {
          try {
            const saved = await axios.get("/api/business-model/my-model");
            if (saved.data?.success && saved.data.businessModel && !localModel) {
              setLocalModel(saved.data.businessModel);
              setBusinessModel(saved.data.businessModel);
              setShowProceedBtn(true);
              if (saved.data.recordId) setRecordId(saved.data.recordId);
            }
          } catch {}
        }
      } catch (err) {
        console.warn("Prefill error:", err.message);
      }
    };

    if (activeUserId) prefill();
  }, [activeUserId, location.state, selectedIdea]);

    /* ----------------------------------------------------------
   ⭐ Persist active idea (same pattern as MVPPlanner/MVPAgent)
---------------------------------------------------------- */

        useEffect(()=>{

        if(selectedIdea?.ideaTitle){

        localStorage.setItem(
        "activeIdeaTitle",
        selectedIdea.ideaTitle
        );

        }

        },[selectedIdea]);

  /* ----------------------------------------------------------
     GENERATE BUSINESS MODEL (NO AUTOSAVE)
  ---------------------------------------------------------- */
  const handleGenerate = async () => {
    const ideaId = selectedIdea?.ideaId || location.state?.ideaId || null;
    setLoading(true);

    try {
      const payload = {
        ideaId,
        recordId,
        startupName:
        selectedIdea?.ideaTitle ||
        selectedIdea?.title ||
        localStorage.getItem("activeIdeaTitle") ||
        formData.startupName ||
        "",
        description: formData.description,
        industry: formData.industry,
        idea:
          selectedIdea?.solution ||
          selectedIdea?.description ||
          formData.description,
        mvpPlan,
        mvpAgent,
        refineWithAI: true,
      };

      const { data } = await axios.post("/api/business-model/generate", payload);

      const model = data.businessModel;
      const returnedId = data.recordId;

      if (model) {
        setLocalModel(model);
        setBusinessModel(model);
        markModuleComplete("businessModel", model);
        setShowProceedBtn(true);
        setLoadedFromDB(false);   // ⭐ ADD
      }

      if (returnedId) setRecordId(returnedId);

      const popup = await triggerAIPopup({
        userId: activeUserId,
        moduleName: "Business Model Builder",
        stepName: "Model Generated",
        context: "User generated business model",
        ideaId,
        recordId: returnedId,
      });

      if (popup) setPopupMessage(popup);
    } catch (err) {
      console.error(err);
      alert("Failed to generate business model.");
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------------------------------
     NAVIGATION
  ---------------------------------------------------------- */
  const goToPlanner = () =>
    navigate("/mvp-planner", { state: { ideaId: selectedIdea?.ideaId } });

  const goToAgent = () =>
    navigate("/mvp-agent", { state: { ideaId: selectedIdea?.ideaId } });

  const goToGTM = () => {
    if (!localModel) return alert("Generate a business model first.");
    navigate("/gtm-accelerator", {
      state: {

      idea:selectedIdea,   // ⭐ THIS IS THE FIX

      businessModel: localModel,

      startupName:
      selectedIdea?.ideaTitle ||
      selectedIdea?.title ||
      localStorage.getItem("activeIdeaTitle") ||
      formData.startupName ||
      "",

      ideaId: selectedIdea?.ideaId,

      recordId,

      }
    });
  };

  /* ----------------------------------------------------------
   DOWNLOAD PDF ⭐ ADD THIS BLOCK
---------------------------------------------------------- */

        const downloadPDF = async () => {

        if(!selectedIdea?.ideaId){

        alert("Idea missing");

        return;

        }

        try{

        setLoadingExport(true);

        const response = await axios.post(

        "/api/pdf/business-model",

        {
        ideaId:selectedIdea.ideaId,
        recordId
        },

        {
        responseType:"blob"
        }

        );

        const blob = new Blob(
        [response.data],
        {type:"application/pdf"}
        );

        const url =
        window.URL.createObjectURL(blob);

        const link =
        document.createElement("a");

        link.href = url;

        link.download =
        buildMDIHPdfName(startupName,"Business_Model");

        document.body.appendChild(link);

        link.click();

        link.remove();

        }
        catch(err){

        console.error(
        "PDF download failed:",
        err
        );

        alert("Failed to download PDF");

        }
        finally{

        setLoadingExport(false);

        }

        };
  /* ----------------------------------------------------------
     RENDER EDITABLE BLOCKS
  ---------------------------------------------------------- */
  const renderBlock = (title, content, keyName) => (
    <div key={keyName} className="p-4 bg-white rounded-lg shadow">
      <h3 className="font-semibold text-blue-900">{title}</h3>

      {Array.isArray(content) ? (
        content.map((item, idx) => (
          <input
            key={`${keyName}-${idx}`}
            className="block w-full mt-2 p-2 border border-slate-300 rounded text-sm text-slate-700 bg-slate-50"
            defaultValue={typeof item === "string" ? item : item?.description}
            readOnly
          />
        ))
      ) : (
        <p className="mt-2 text-sm text-gray-600">No data</p>
      )}
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">

        <ModuleHeader

        title="Business Model"

        subtitle="Translate your validated idea into a structured, investor-ready business model."

        currentModule="businessModel"

        isLoadedFromDB={loadedFromDB}

        />

        {/* ACTIVE IDEA */}

        <div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6">

        <b>Startup:</b> {startupName}

        </div>

        {/* ===== ORIGINAL HEADING (PRESERVED) ===== */}
        

        {/* ACTIONS */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Generating..." : "Generate Business Model"}
          </button>

          <button
            onClick={goToPlanner}
            className="text-blue-600 text-sm hover:underline"
          >
            ← Back to MVP Planner
          </button>

          <button
            onClick={goToAgent}
            className="text-blue-600 text-sm hover:underline"
          >
            ← Back to MVP Agent
          </button>
        </div>
        
        <AIPopupModal message={popupMessage} onClose={() => setPopupMessage("")} />

        {/* MODEL */}
        {localModel && (
          <div
            ref={modelRef}
            className="space-y-6 bg-white border border-slate-200 rounded-xl p-6 mt-6"
          >
            <h3 className="text-xl font-semibold text-green-700">

            {loadedFromDB ?

            "📦 Previously Generated Business Model"

            :

            "📦 Newly Generated Business Model"

            }

            </h3>
            <button
            onClick={downloadPDF}
            disabled={loadingExport}
            className="mt-2 bg-green-600 hover:bg-blue-700
                        text-white px-6 py-3 rounded-lg font-semibold
                        inline-flex items-center gap-2 transition
                        disabled:opacity-50"
            >

            <Download size={18}/>

            {loadingExport
            ? "Preparing PDF..."
            : "Download Business Model Report"}

            </button>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(localModel).map(([key, value]) =>
                renderBlock(key.replace(/([A-Z])/g, " $1"), value, key)
              )}
            </div>

            <p className="text-sm text-slate-600">
              Record: {recordId ? `Saved (ID: ${recordId})` : "Not saved"}
            </p>

            {/* ===== STRATEGIC CTA ===== */}
            {showProceedBtn && (
              <div className="pt-4 space-y-2 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-blue-900">
                  Ready to Plan Your Go-To-Market Strategy?
                </h3>

                <p className="text-sm text-slate-600 max-w-xl">
                  Convert your validated business model into a concrete launch,
                  acquisition, and growth strategy.
                </p>

                <button
                  onClick={goToGTM}
                  className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                  Go To GTM Accelerator →
                </button>
              </div>
            )}
          </div>
        )}

        {/* LEARNING CTA */}
        {learningLinks["business-model"] && (
          <div className="pt-6 border-t border-slate-200">
            <button
              onClick={() =>
                navigate(
                  `/learning-mentorship?video=${learningLinks["business-model"].videoId}`
                )
              }
              className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm hover:bg-blue-700 transition"
            >
              🎓 Learn Business Model Design
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessModelBuilder;
